import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GlobalsService } from '../../services/globals.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';

@Component({
	selector: 'app-currencies',
	templateUrl: './currencies.component.html',
	styleUrls: ['./currencies.component.scss']
})
export class CurrenciesComponent implements OnInit {
	@ViewChild(MatSort) sort: MatSort;
	@ViewChild(MatPaginator) paginator: MatPaginator;

	currencies: MatTableDataSource<any> = new MatTableDataSource([]);
	displayedColumns: string[] = ['currabrev', 'currency', 'symbol_left', 'symbol_right', 'decimalplaces', 'rate', 'status', 'actions'];
	form: UntypedFormGroup;
	editing: any = null;
	color: string;
	defaultCurrabrev: string = '';

	constructor(
		private fb: UntypedFormBuilder,
		private globalsService: GlobalsService,
		private globalSearchService: GlobalSearchService,
		private location: Location,
		private modal: NgbModal
	) {
		this.color = this.globalSearchService.getColor();
		this.form = this.fb.group({
			currabrev: ['', [Validators.required, Validators.maxLength(3)]],
			currency: ['', Validators.required],
			symbol_left: [''],
			symbol_right: [''],
			decimalplaces: [2, [Validators.required, Validators.min(0), Validators.max(6)]],
			rate: [1, [Validators.required, Validators.min(0)]],
			status: [1]
		});
	}

	ngOnInit(): void {
		this.load();
		this.globalsService.getCompany().subscribe((c: any) => {
			this.defaultCurrabrev = c?.currencydefault || '';
		});
	}

	load(): void {
		this.globalsService.getCurrencies().subscribe((list: any) => {
			this.currencies = new MatTableDataSource(Array.isArray(list) ? list : []);
			this.currencies.sort = this.sort;
			this.currencies.paginator = this.paginator;
		});
	}

	openAdd(tpl: any): void {
		this.editing = null;
		this.form.reset({ currabrev: '', currency: '', symbol_left: '', symbol_right: '', decimalplaces: 2, rate: 1, status: 1 });
		this.form.get('currabrev')?.enable();
		this.modal.open(tpl, { size: 'md' });
	}

	openEdit(tpl: any, row: any): void {
		this.editing = row;
		this.form.patchValue({
			currabrev: row.currabrev,
			currency: row.currency,
			symbol_left: row.symbol_left || '',
			symbol_right: row.symbol_right || '',
			decimalplaces: row.decimalplaces ?? 2,
			rate: row.rate ?? 1,
			status: row.status ?? 1
		});
		this.form.get('currabrev')?.disable();
		this.modal.open(tpl, { size: 'md' });
	}

	save(): void {
		if (this.form.invalid) { return; }
		const v = this.form.getRawValue();
		if (this.editing) {
			this.globalsService.updateCurrency({
				currency_id: this.editing.currency_id,
				currency: v.currency,
				symbol_left: v.symbol_left,
				symbol_right: v.symbol_right,
				decimalplaces: v.decimalplaces,
				rate: v.rate,
				status: v.status
			}).subscribe({
				next: () => {
					this.globalSearchService.showNotification('Currency updated', 'success', 'bottom', 'right');
					this.load();
					this.modal.dismissAll();
				},
				error: (err) => {
					this.globalSearchService.showNotification(err?.error?.error || 'Update failed', 'danger', 'bottom', 'right');
				}
			});
		} else {
			this.globalsService.addCurrency({
				currabrev: (v.currabrev || '').trim().toUpperCase(),
				currency: v.currency,
				symbol_left: v.symbol_left,
				symbol_right: v.symbol_right,
				decimalplaces: v.decimalplaces,
				rate: v.rate,
				status: v.status
			}).subscribe({
				next: () => {
					this.globalSearchService.showNotification('Currency added', 'success', 'bottom', 'right');
					this.load();
					this.modal.dismissAll();
				},
				error: (err) => {
					this.globalSearchService.showNotification(err?.error?.error || 'Add failed', 'danger', 'bottom', 'right');
				}
			});
		}
	}

	deactivate(row: any): void {
		if (row.currabrev === this.defaultCurrabrev) {
			this.globalSearchService.showNotification('Cannot deactivate the company default currency', 'danger', 'bottom', 'right');
			return;
		}
		if (!confirm('Deactivate currency ' + row.currabrev + '?')) { return; }
		this.globalsService.deactivateCurrency({ currency_id: row.currency_id }).subscribe({
			next: () => {
				this.globalSearchService.showNotification('Currency deactivated', 'success', 'bottom', 'right');
				this.load();
			},
			error: (err) => {
				this.globalSearchService.showNotification(err?.error?.error || 'Deactivate failed', 'danger', 'bottom', 'right');
			}
		});
	}

	activate(row: any): void {
		this.globalsService.activateCurrency({ currency_id: row.currency_id }).subscribe({
			next: () => {
				this.globalSearchService.showNotification('Currency activated', 'success', 'bottom', 'right');
				this.load();
			},
			error: (err) => {
				this.globalSearchService.showNotification(err?.error?.error || 'Activate failed', 'danger', 'bottom', 'right');
			}
		});
	}

	back(): void {
		this.location.back();
	}

	statusLabel(status: number | string): string {
		return (status === 1 || status === '1') ? 'Active' : 'Inactive';
	}
}
